import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Pizza, UserPlus, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    businessName: '', address: '',
    emailOptIn: false, smsOptIn: false,
  });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const allRequired = form.name.trim() && form.email.trim() && form.password.length >= 6 &&
    form.phone.trim() && form.businessName.trim() && form.address.trim() &&
    form.emailOptIn && form.smsOptIn;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRequired) return;
    setLoading(true);
    setError('');
    const result = await register(form);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="min-h-screen bg-[#FFF8E7] flex items-center justify-center p-4">
      <div className="pizza-card p-6 sm:p-8 w-full max-w-lg" data-testid="register-card">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center mx-auto mb-3 shadow-[3px_3px_0px_0px_rgba(42,42,42,1)]">
            <Pizza className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-3xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="register-title">
            Create Your Account
          </h1>
          <p className="text-sm text-[#2A2A2A]/50 mt-1">Save your data and come back anytime.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Full Name <span className="text-[#E23D28]">*</span>
              </label>
              <input type="text" required value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Mike Romano" className="pizza-input w-full text-sm" data-testid="register-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Business Name <span className="text-[#E23D28]">*</span>
              </label>
              <input type="text" required value={form.businessName} onChange={e => set('businessName', e.target.value)}
                placeholder="Mike's Pizza Palace" className="pizza-input w-full text-sm" data-testid="register-business" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Business Address <span className="text-[#E23D28]">*</span>
            </label>
            <input type="text" required value={form.address} onChange={e => set('address', e.target.value)}
              placeholder="123 Main St, Brooklyn, NY 11201" className="pizza-input w-full text-sm" data-testid="register-address" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Email <span className="text-[#E23D28]">*</span>
              </label>
              <input type="email" required value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="mike@pizzapalace.com" className="pizza-input w-full text-sm" data-testid="register-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
                Cell Phone <span className="text-[#E23D28]">*</span>
              </label>
              <input type="tel" required value={form.phone} onChange={e => set('phone', e.target.value)}
                placeholder="(555) 123-4567" className="pizza-input w-full text-sm" data-testid="register-phone" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Password <span className="text-[#E23D28]">*</span>
            </label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} required minLength={6} value={form.password}
                onChange={e => set('password', e.target.value)}
                placeholder="At least 6 characters" className="pizza-input w-full text-sm pr-10" data-testid="register-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40 hover:text-[#2A2A2A]">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Double opt-in — REQUIRED */}
          <div className="bg-[#FFF8E7] border-2 border-[#2A2A2A]/10 rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-[#2A2A2A]/60 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              Communication Consent <span className="text-[#E23D28]">*Required</span>
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.emailOptIn} onChange={e => set('emailOptIn', e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-2 border-[#2A2A2A] accent-[#E23D28]" required
                data-testid="register-email-optin" />
              <span className="text-xs text-[#2A2A2A]/70 leading-relaxed">
                <strong>Yes</strong>, I agree to receive helpful tips on restaurant profitability, cost-saving strategies, and Pizza-lator updates via <strong>email</strong>. You can unsubscribe anytime.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.smsOptIn} onChange={e => set('smsOptIn', e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-2 border-[#2A2A2A] accent-[#E23D28]" required
                data-testid="register-sms-optin" />
              <span className="text-xs text-[#2A2A2A]/70 leading-relaxed">
                <strong>Yes</strong>, I consent to receive occasional <strong>SMS/text messages</strong> with tips and updates from Mikey Mozzarella's Pizza-lator. Message & data rates may apply. Reply STOP to opt out at any time. Max 4 msgs/month.
              </span>
            </label>
          </div>

          {error && <p className="text-sm text-[#E23D28]" data-testid="register-error">{error}</p>}

          <button type="submit" disabled={!allRequired || loading}
            className="pizza-btn w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="register-submit-btn">
            {loading ? 'Creating Account...' : <><UserPlus className="w-4 h-4" /> Create Account</>}
          </button>

          <p className="text-center text-sm text-[#2A2A2A]/50">
            Already have an account?{' '}
            <Link to="/login" className="text-[#E23D28] hover:underline font-medium" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
